/** @jsxImportSource @emotion/react */

import { FC, FocusEvent, useState } from 'react'
import Typography from '@mui/material/Typography'
import { Form as FinalForm } from 'react-final-form'
import arrayMutators from 'final-form-arrays'
import createDecorator from 'final-form-focus'
import { SubmissionErrors } from 'final-form'
import { useNavigate } from 'react-router-dom'
import { generate as generateUniqueId } from 'randomstring'
import omit from 'lodash/omit'
import { Button, Accordion, AccordionDetails, AccordionSummary, IconButton } from '@mui/material'
import slugify from 'slugify'
import { DropzoneField, ImageField } from 'components/DropzoneField'
import { InputField } from 'components/Input'
import { Input } from 'components/Input/Input'
import { prepareEditorHtml } from 'components/Editor'
import { AdvancedFieldArray } from 'components/AdvancedFieldArray/AdvancedFieldArray'
import { ConfirmationModal } from 'components/Modals/ConfirmationModal/ConfirmationModal'
import { ReactComponent as ChevronDownIcon } from 'assets/icons/chevron-down.svg'
import { ReactComponent as TrashIcon } from 'assets/icons/trash.svg'
import { setFieldData } from 'utils/setFieldData'
import { validateRequired } from 'utils/validation'
import { DENTAL_SCHOOL_MEDIA_PATH_S3 } from 'constants/images'
import { CreateDentalSchoolInput } from '__generatedTypes__/globalTypes'
import { findOneDentalSchool_findOneDentalSchool as DentalSchool } from '__generatedTypes__/findOneDentalSchool'
import { CheckboxField } from 'components/Checkbox'
import { isImageUploadError, loadImageFile, uploadImageFile } from 'utils/imageFile'
import { toast } from 'utils/toast'
import { SlugInputField } from 'components/SlugInputField/SlugInputField'
import { SectionFieldArray, Section } from '../SectionFieldArray'
import * as sectionFieldClasses from '../SectionFieldArray/styles'
import * as classes from './styles'

type ImageFieldUpd = ImageField & { shortSrc?: string }

export type FormValues = {
  platform: {
    id: string
  }
  isOptometry?: boolean
  slug?: string
  coverImage?: ImageFieldUpd[]
  isFeaturedExperts?: boolean
  title?: string
  description?: string
  infoFirstColumn: string[]
  infoSecondColumn: string[]
  details: Section[]
}

const DROPZONE_OPTIONS = {
  accept: {
    'image/*': [],
  },
}

const focusOnErrors = createDecorator<FormValues, Partial<FormValues>>()

export const getInitialValues = async (platformId: string, dentalSchool?: DentalSchool | null): Promise<FormValues> => {
  const coverImage = await loadImageFile(dentalSchool?.getCoverImage)

  return {
    ...omit(dentalSchool ?? {}, '__typename'),
    coverImage: coverImage && [{ ...coverImage, shortSrc: dentalSchool?.image }],
    infoFirstColumn: dentalSchool?.infoFirstColumn ?? [],
    infoSecondColumn: dentalSchool?.infoSecondColumn ?? [],
    details: (dentalSchool?.details ?? []).map((section) => ({
      ...omit(section, '__typename'),
      id: generateUniqueId(),
    })) as Section[],
    platform: {
      id: platformId,
    },
    isOptometry: dentalSchool?.isOptometry,
  }
}

const uploadImage = async (imageField?: ImageFieldUpd) => {
  if (!imageField) {
    return null
  }

  if (imageField.isUploaded && imageField.shortSrc) {
    return imageField.shortSrc
  }

  return await uploadImageFile(imageField, DENTAL_SCHOOL_MEDIA_PATH_S3, false)
}

export const prepareForSubmit = async (values: FormValues): Promise<CreateDentalSchoolInput> => {
  const [coverImage, details] = await Promise.all([
    uploadImage(values.coverImage?.[0]),
    Promise.all(
      (values.details ?? []).map(async (detailsSection) => {
        if (detailsSection.table) {
          return {
            ...omit(detailsSection, 'id'),
          }
        }

        const descriptionAsHtml = await prepareEditorHtml(detailsSection.description ?? '', DENTAL_SCHOOL_MEDIA_PATH_S3)

        return {
          ...omit(detailsSection, 'id'),
          description: descriptionAsHtml,
          table: undefined,
        }
      }) ?? []
    ),
  ])

  return {
    platform: values.platform,
    isOptometry: values.isOptometry,
    image: coverImage ?? '',
    isFeaturedExperts: values.isFeaturedExperts,
    title: values.title,
    description: values.description,
    slug: values.slug,
    infoFirstColumn: values.infoFirstColumn ?? [],
    infoSecondColumn: values.infoSecondColumn ?? [],
    details: details ?? [],
  }
}

type Props = {
  initialValues: FormValues
  onSubmit: (values: CreateDentalSchoolInput) => SubmissionErrors | Promise<SubmissionErrors> | void
  isOptometry: boolean
  isEdit?: boolean
  onDelete?: () => Promise<void>
}

export const Form: FC<Props> = ({ initialValues, onSubmit, isOptometry, isEdit, onDelete }) => {
  const navigate = useNavigate()
  const [isBackgroundInfoExpanded, setIsBackgroundInfoExpanded] = useState(true)
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [isSlugEdited, setIsSlugEdited] = useState(isEdit)

  const handleTitleChange =
    (onChange: (name: keyof FormValues, value: string) => void) => (e: FocusEvent<HTMLInputElement>) => {
      if (isSlugEdited) {
        return
      }

      onChange('slug', slugify(e.target.value, { lower: true }))
    }

  const handleSlugBlur =
    (onChange: (name: keyof FormValues, value: string) => void) => (e: FocusEvent<HTMLInputElement>) => {
      onChange('slug', slugify(e.target.value, { lower: true }))
    }

  const handleSubmit = async (values: FormValues) => {
    if (submitting) {
      return
    }

    setSubmitting(true)
    try {
      const preparedValues = await prepareForSubmit(values)
      return await onSubmit(preparedValues)
    } catch (e) {
      console.error(`Save ${isOptometry ? 'Optometry' : 'Dental'} School failed:`, e)

      toast.error(isImageUploadError(e) ? e.message : `Failed to save ${isOptometry ? 'Optometry' : 'Dental'} School`)
    } finally {
      setSubmitting(false)
    }
  }

  const handleDelete = async () => {
    if (submitting) {
      return
    }

    setSubmitting(true)
    await onDelete?.()
    setSubmitting(false)
    setIsConfirmationModalOpen(false)
  }

  return (
    <section css={classes.container}>
      <FinalForm<FormValues>
        initialValues={initialValues}
        mutators={{ ...arrayMutators, setFieldData }}
        decorators={[focusOnErrors]}
        onSubmit={handleSubmit}
        render={({ handleSubmit, form, submitting }) => {
          const slugInputProps = {
            name: 'slug',
            validate: validateRequired,
            label: 'Slug',
            placeholder: 'Enter slug',
            fullWidth: true,
            onBlur: handleSlugBlur(form.change),
            onChange: () => setIsSlugEdited(true),
          }

          return (
            <form css={classes.form} onSubmit={handleSubmit}>
              <div css={classes.header}>
                <Typography css={classes.title} variant="title3">
                  {isEdit ? 'Edit' : 'Create'} {isOptometry ? 'Optometry' : 'Dental'} School
                </Typography>
                <div css={classes.actionButtons}>
                  {isEdit && onDelete && (
                    <Button
                      variant="outlined"
                      color="blue"
                      size="small"
                      type="button"
                      css={classes.deleteButton}
                      onClick={() => setIsConfirmationModalOpen(true)}
                      disabled={submitting}
                      data-testid="delete-school-button"
                    >
                      <TrashIcon />
                      Delete
                    </Button>
                  )}
                  <Button
                    variant="outlined"
                    color="blue"
                    size="small"
                    type="button"
                    disabled={submitting}
                    onClick={() => navigate(-1)}
                    data-testid="cancel-school-button"
                  >
                    Cancel
                  </Button>
                  <Button
                    size="small"
                    color="blue"
                    type="submit"
                    disabled={submitting}
                    data-testid="submit-school-button"
                  >
                    {isEdit ? 'Save Edits' : 'Create'}
                  </Button>
                </div>
              </div>

              <CheckboxField name="isFeaturedExperts" label="Display Featured Experts Banner" />
              <DropzoneField
                label="Cover Image"
                name="coverImage"
                validate={validateRequired}
                dropzoneOptions={DROPZONE_OPTIONS}
                options={{
                  maxFiles: 1,
                  sliceFromEnd: true,
                }}
              />
              <InputField
                name="title"
                validate={validateRequired}
                label="Title"
                placeholder="Enter Title"
                onBlur={handleTitleChange(form.change)}
                fullWidth
                data-testid="title-school-field"
              />
              <InputField
                name="description"
                validate={validateRequired}
                label="Description"
                placeholder="Enter Description"
                fullWidth
                data-testid="description-school-field"
              />
              {isEdit ? <SlugInputField {...slugInputProps} /> : <InputField {...slugInputProps} />}

              <Accordion expanded={isBackgroundInfoExpanded} css={sectionFieldClasses.accordion}>
                <AccordionSummary css={sectionFieldClasses.accordionSummary}>
                  <Typography variant="title3" css={sectionFieldClasses.title}>
                    Background Information
                  </Typography>
                  <IconButton
                    onClick={() => setIsBackgroundInfoExpanded((prev) => !prev)}
                    data-testid="expand-background-information"
                  >
                    <ChevronDownIcon
                      css={[
                        sectionFieldClasses.iconChevron,
                        isBackgroundInfoExpanded && sectionFieldClasses.iconChevronOpen,
                      ]}
                    />
                  </IconButton>
                </AccordionSummary>
                <AccordionDetails css={sectionFieldClasses.accordionDetails}>
                  <div css={classes.columns}>
                    <AdvancedFieldArray<string>
                      name="infoFirstColumn"
                      validate={(value) => ((value?.length ?? 0) < 1 ? 'Should be at least one item' : undefined)}
                      itemName="Item"
                      getEmptyItem={() => ''}
                      noItemsText="No items added"
                      isSortable
                      validateField={validateRequired}
                      css={classes.column}
                      renderField={({ name, value, onChange, editable, index, error }) =>
                        editable ? (
                          <Input
                            name={name}
                            value={value ?? ''}
                            onChange={onChange}
                            placeholder={`Enter Item #${index + 1}`}
                            size="small"
                            color="secondary"
                            error={!!error}
                            fullWidth
                          />
                        ) : (
                          <div>{value ?? ''}</div>
                        )
                      }
                      removeConfirmation
                    />
                    <AdvancedFieldArray<string>
                      name="infoSecondColumn"
                      validate={(value) => ((value?.length ?? 0) < 1 ? 'Should be at least one item' : undefined)}
                      itemName="Item"
                      getEmptyItem={() => ''}
                      noItemsText="No items added"
                      isSortable
                      validateField={validateRequired}
                      css={classes.column}
                      renderField={({ name, value, onChange, editable, index, error }) =>
                        editable ? (
                          <Input
                            name={name}
                            value={value ?? ''}
                            onChange={onChange}
                            placeholder={`Enter Item #${index + 1}`}
                            size="small"
                            color="secondary"
                            error={!!error}
                            fullWidth
                          />
                        ) : (
                          <div>{value ?? ''}</div>
                        )
                      }
                      removeConfirmation
                    />
                  </div>
                </AccordionDetails>
              </Accordion>

              <SectionFieldArray name="details" itemName="Section" noItemsText="No sections added" removeConfirmation />
            </form>
          )
        }}
      />

      <ConfirmationModal
        open={isConfirmationModalOpen}
        title="Deleting Content"
        subtitle={`Are you sure you want to delete this ${isOptometry ? 'Optometry' : 'Dental'} School?`}
        onClose={() => setIsConfirmationModalOpen(false)}
        onConfirm={handleDelete}
        confirmText="Confirm Deleting"
      />
    </section>
  )
}
